import { useCallback, useEffect, useRef, useState } from "react";
import DatePickerField from "../../../components/form/date-picker";
import SelectField from "../../../components/form/select-field"
import AuthLayout from "../../../components/layouts/auth"
import SearchableDropdown from "../../../components/form/searchable-dropdown";
import Button from "../../../components/form/button";
import { ErrorFlash } from "../../../components/helper/flash-notification";
import Naira from "../../../components/naira";
import { getBackUrl, getTransactionColorAndName, getTransactionTypeName, numberWithCommas } from "../../../lib/helper-support";
import moment from "moment";
import { ExternalLink, Minus } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import TablePagination from "../../../components/table/table-pagination";
import { useQueryState } from "../../../hooks/useQueryState";
import { useDebounced } from "../../../hooks/useDebounce";
import { useUsers } from "../../../composables/users/useUsers";
import { useApi } from "../../../providers/ApiProvider";

const UserMetric: React.FC = () => {

    const api = useApi();

    type TransactionsProp = {
        reference: string,
        date: string,
        type: string,
        sender: string,
        receiver: string | null,
        amount: string,
        status: string,
        statusColor: string,
        provider?: string,
        narration?: string,
        cashir_charge?: string,
    };

    type TransactionDataProp = {
        items: TransactionsProp[],
        page: number,
        nextPage: number,
        hasMorePages: boolean,
        totalPages: number,
        total?: number,
    };

    const [params] = useSearchParams();

    const [search, setSearch] = useQueryState<string>(
        "search",
        "",
        (val) => val || "",
        (val) => val.toString()
    );

    const [period, setPeriod] = useQueryState<string>(
        "period",
        "",
        (val) => val || "",
        (val) => val.toString()
    );

    const [dateRange, setDateRange] = useQueryState<string>(
        "date_range",
        "",
        (val) => val || "",
        (val) => val ?? ""
    );

    const [currentPage, setCurrentPage] = useQueryState<number>(
        "page",
        1,
        (val) => (val ? parseInt(val, 10) : 1),
        (val) => val.toString()
    );

    const searchDebounced = useDebounced({ value: search });

    const [transactions, setTransactions] = useState<TransactionsProp[]>([]);
    const [transactionData, setTransactionData] = useState<TransactionDataProp | undefined>(undefined);
    const [tab, setTab] = useState<string>('metric');
    const [metrics, setMetrics] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const PeriodOption = [
        { name: 'Today', value: 'today' },
        { name: 'Yesterday', value: 'yesterday' },
        { name: 'This month', value: 'this_month' },
        { name: 'Last month', value: 'last_month' },
        { name: 'Date range', value: 'date_range' }
    ];

    const [user, setUser] = useState<UserAccountType>();

    const initializedFromParams = useRef(false);
    const autoFetchedFromParams = useRef(false);

    const paramSearch = params.get('search');

    useEffect(() => {
        if (paramSearch && !initializedFromParams.current) {
            setSearch(paramSearch);
        }
    }, [paramSearch, setSearch]);

    const { data: users = [] } = useUsers({
        page: 1,
        search_query: searchDebounced,
        status: '',
    });

    useEffect(() => {
        if (!paramSearch || initializedFromParams.current) return;
        if (!users?.items?.length) return;

        const selectedUser = users.items.find((u: any) =>
            u.username === paramSearch ||
            u.email === paramSearch ||
            u.display_name?.includes(paramSearch) ||
            u.phone_number === paramSearch
        );

        if (selectedUser) {
            setUser(selectedUser);
            setSearch(selectedUser.display_name);
            initializedFromParams.current = true;
        }
    }, [users, paramSearch, setSearch]);

    const generateMetrics = useCallback(async (page = currentPage) => {
        const dates = dateRange?.split('to') ?? null;
        if (period === 'date_range' && !dates) {
            ErrorFlash('Please select a date range');
            return;
        }

        setLoading(true);

        const request = await api.POST({
            endpoint: `/generateUserMetric?page=${page}`,
            payload: {
                period,
                identifier: user?.phone_number,
                date_from: dates ? dates[0] : null,
                date_to: dates ? dates[1] : null,
            }
        });

        setLoading(false);

        if (!request) return;

        setMetrics(request.data);

        const data = request.data.transactions;
        setTransactionData(data);

        const newTransactions = data.items.map((transaction: any) => ({
            date: moment(transaction.created_at).format('h:mm a, MMM D'),
            reference: transaction.reference,
            type: getTransactionTypeName(transaction),
            sender: transaction.sender ? transaction.sender.name : <Minus />,
            receiver: transaction.receiver ? transaction.receiver.name : <Minus />,
            amount: numberWithCommas(transaction.amount),
            status: transaction.status,
            statusColor: getTransactionColorAndName(transaction).color,
            provider: transaction.provider,
            narration: transaction.narration,
            cashir_charge: transaction.cashir_charge,
        }));

        setTransactions(newTransactions);
    }, [api, currentPage, dateRange, period, user]);

    useEffect(() => {
        if (autoFetchedFromParams.current) return;
        if (!user || !period) return;
        if (period === 'date_range' && !dateRange) return;

        autoFetchedFromParams.current = true;
        generateMetrics(currentPage);
    }, [user, period, dateRange, currentPage, generateMetrics]);

    useEffect(() => {
        if (!user || !metrics) return;
        generateMetrics(currentPage);
    }, [currentPage]);

    // ── Handlers ────────────────────────────────────────────────────────────────
    const handleGenerateClick = () => {
        setCurrentPage(1);
        generateMetrics(1);
    };

    const handleSelectUser = (identifier: string | null) => {
        if (!identifier) return;

        const selectedUser = users.items.find((u: any) =>
            u.username === identifier ||
            u.email === identifier ||
            u.display_name?.includes(identifier) ||
            u.phone_number === identifier
        );

        if (selectedUser) {
            setUser(selectedUser);
        }
    };

    const handlePeriodChange = (value: string) => {
        setPeriod(value);
        // Clear the stored date range from URL when leaving date_range mode
        if (value !== 'date_range') {
            setDateRange('');
        }
    };

    const handleDateRangeChange = (val: any) => {
        setDateRange(val ?? '');
    };

    const GetBackUrl = () => {
    const link = decodeURIComponent(getBackUrl());
    
    const oldValue = encodeURIComponent(searchDebounced).replace(/%20/g, "+");
    const newValue = encodeURIComponent(user?.username ?? "").replace(/%20/g, "+");

    return encodeURIComponent(link.replace(oldValue, newValue));
};

    return (
        <AuthLayout>
            <div>
                <div className="items-center space-y-5 lg:justify-between lg:flex md:flex md:space-y-0">
                    <div className='flex items-center space-x-6'>
                        <h1 className="page-title flex flex-wrap gap-2 items-center">
                            <span>User Metrics -</span>
                            {user && <Link to={`/user-accounts/${user?.phone_number}`} className="underline text-[20px] flex items-center gap-1 text-gray-600">
                                <span>{user?.display_name}</span>
                                <ExternalLink size={16} />
                            </Link>}
                        </h1>
                    </div>
                </div>
                <div className="items-center gap-4 flex-wrap md:flex md:space-y-0 mt-5 space-y-5">
                    <div>
                        <SearchableDropdown
                            placeholder="select user"
                            inputClass="w-full"
                            value={search}
                            onChangeInput={(e) => setSearch(e.target.value)}
                            options={users?.items?.map((item: UserAccountType) => ({ value: item.phone_number, name: `${item.display_name}` }))}
                            onSelectedOption={handleSelectUser}
                            required={false}
                        />
                    </div>
                    <div>
                        <SelectField
                            value={period}
                            onChangeInput={handlePeriodChange}
                            inputClass="w-full md:w-[15em]"
                            placeholder={'Select period'}
                            options={PeriodOption}
                        />
                    </div>
                    <div>
                        {period === 'date_range' && (
                            <DatePickerField
                                value={dateRange}
                                onChangeInput={handleDateRangeChange}
                                mode="range"
                                inputClass="w-full"
                            />
                        )}
                    </div>
                    <Button
                        disabled={!user}
                        isLoading={loading}
                        onClick={handleGenerateClick}
                        design="primary"
                        text="Generate metrics"
                    />
                </div>
                <div className="mt-10 flex flex-wrap items-center gap-8">
                    <span
                        onClick={() => setTab('metric')}
                        className={`cursor-pointer ${tab === 'metric' && 'text-primary border-b border-primary'}`}
                    >
                        Metric
                    </span>
                    <span
                        onClick={() => setTab('transactions')}
                        className={`cursor-pointer ${tab === 'transactions' && 'text-primary border-b border-primary'}`}
                    >
                        Transactions
                    </span>
                </div>

                {tab === 'metric' && (
                    <div className="grid gap-4 mt-10 lg:grid-cols-3 md:grid-cols-2">
                        {[
                            { label: 'Transactions', key: 'transaction' },
                            { label: 'Inflow', key: 'inflow' },
                            { label: 'Outflow', key: 'outflow' },
                            { label: 'Send cash to bank', key: 'send_cash_to_bank' },
                            { label: 'Send cash to user', key: 'send_cash_to_user' },
                            { label: 'Bill payments', key: 'bills_payment' },
                        ].map(({ label, key }) => (
                            <div key={key} className='p-5 bg-primary-light rounded-[13px] relative h-[10em] md:h-[12em]'>
                                <div className='flex items-center justify-between'>
                                    <h4 className='font-[600] text-[13px]'>{label}</h4>
                                </div>
                                <div className='absolute top-14'>
                                    <h4 className='text-blue-black font-[600] text-[2.5em]'>
                                        <Naira />
                                        {numberWithCommas(metrics?.[key]?.amount ?? 0)}.
                                        <span className='text-[11px]'>
                                            {numberWithCommas(metrics?.[key]?.amount ?? 0, true, true)}
                                        </span>
                                    </h4>
                                    <p className='text-primary text-[1.5em] font-[600] mt-2'>
                                        {metrics?.[key]?.count}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === 'transactions' && (
                    <>
                        <div className='relative overflow-x-auto'>
                            <table className='mt-10 text-tertiary font-[600] text-[14px] w-full relative z-10 whitespace-nowrap'>
                                <thead>
                                    <tr className='border-b border-[#E6EAF0]'>
                                        <td className="px-2 py-3" />
                                        <td className="px-4 py-3">Date</td>
                                        <td className="px-6 py-3">Type</td>
                                        <td className="px-6 py-3">Infrastructure</td>
                                        <td className="px-6 py-3">Status</td>
                                        <td className="px-6 py-3">Sender</td>
                                        <td className="px-6 py-3">Receiver</td>
                                        <td className="px-6 py-3">Amount</td>
                                        <td className="px-6 py-3">Cashir Charge</td>
                                        <td className="px-6 py-3">Narration</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions?.map((transaction, index) => (
                                        <tr key={index} className='border-b border-[#E6EAF0] relative'>
                                            <td className="px-2 py-3">
                                                <Link
                                                    to={`/transactions/${transaction.reference}?back=${GetBackUrl()}`}
                                                    className='absolute top-0 bottom-0 left-0 w-full h-full hover:bg-[#E6EAF0] opacity-[0.2]'
                                                />
                                            </td>
                                            <td className="w-full px-4 py-3">{transaction.date}</td>
                                            <td className="w-full px-6 py-3">
                                                <div className='flex bg-[#F8FAFB] justify-center w-full py-2 px-6 rounded-full'>
                                                    <span className='first-letter:capitalize'>{transaction.type}</span>
                                                </div>
                                            </td>
                                            <td className="w-full px-6 py-3 capitalize">{transaction.provider ?? 'Cashir'}</td>
                                            <td className="px-6 py-3">
                                                <div className='flex items-center space-x-2 font-[400] bg-[#F8FAFB] justify-center w-[10em] py-2 rounded-full'>
                                                    <span style={{ backgroundColor: transaction.statusColor }} className='w-[9px] h-[9px] rounded-full' />
                                                    <span className='text-[#252631]'>{transaction.status}</span>
                                                </div>
                                            </td>
                                            <td className="w-full px-6 py-3">{transaction.sender ?? <Minus size={14} />}</td>
                                            <td className="w-full px-6 py-3">{transaction.receiver ?? <Minus size={14} />}</td>
                                            <td className="px-6 py-3"><Naira /> {transaction.amount}</td>
                                            <td className="w-full px-6 py-3 capitalize">{transaction.cashir_charge ?? 0}</td>
                                            <td className="w-full px-6 py-3 capitalize">{transaction.narration ?? <Minus size={14} />}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className='mt-5'>
                            <TablePagination
                                totalPages={transactionData?.totalPages ?? 0}
                                onPageChange={(page) => setCurrentPage(page)}
                                currentPage={transactionData?.page}
                            />
                        </div>
                    </>
                )}
            </div>
        </AuthLayout>
    );
}

export default UserMetric;